import React, {ComponentType, FC} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {getThumbnailSrc} from './getThumbnailSrc';

interface Props {
  className?: string;
  ComponentEnd?: ComponentType<React.PropsWithChildren>;
  dataTestId?: string;
  label: string;
  large?: boolean;
  onClick?: () => void;
  path?: string;
  selected?: boolean;
  thumbnailUrl?: string;
}

export const ColorOption: FC<Props> = ({
  className,
  ComponentEnd,
  dataTestId,
  label,
  large,
  onClick,
  path,
  selected,
  thumbnailUrl,
}) => {
  const thumbnailSrc = getThumbnailSrc(thumbnailUrl);

  return (
    <Wrapper
      className={className}
      dataTestId={dataTestId}
      onClick={onClick}
      path={path}
      selected={selected}
    >
      <ContentStart>
        <Radio
          $hasThumbnail={Boolean(thumbnailUrl)}
          $large={Boolean(large)}
          $selected={Boolean(selected)}
        >
          {Boolean(thumbnailUrl) && (
            <Thumbnail
              data-testid="colorOptionThumbnail"
              decoding="async"
              importance="low"
              loading="lazy"
              src={thumbnailSrc}
            />
          )}
        </Radio>
        <Label
          data-testid="colorOptionLabel"
          $large={Boolean(large)}
          $selected={Boolean(selected)}
        >
          {label}
        </Label>
      </ContentStart>
      {ComponentEnd && <ComponentEnd />}
    </Wrapper>
  );
};

const Wrapper: FC<
  React.PropsWithChildren<
    Omit<Props, 'ComponentEnd' | 'label' | 'large' | 'thumbnailUrl'>
  >
> = ({
  children,
  className,
  dataTestId = 'colorOption',
  onClick,
  path,
  selected,
}) => {
  if (path) {
    return (
      <LinkWrapper
        className={className}
        data-testid={`${dataTestId}${selected ? '_selected' : ''}`}
        onClick={onClick}
        replace
        to={path}
        aria-label={undefined}
      >
        {children}
      </LinkWrapper>
    );
  }

  return (
    <ContentWrapper
      className={className}
      data-testid={`${dataTestId}${selected ? '_selected' : ''}`}
      onClick={onClick}
    >
      {children}
    </ContentWrapper>
  );
};

const sharedWrapperStyles = css`
  align-items: center;
  border: none;
  background-color: transparent;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  height: 50px;
  outline: none;
  padding: 10px 0 10px 20px;
  width: 100%;
`;

const LinkWrapper = styled(UnstyledLink)`
  ${sharedWrapperStyles};
  cursor: pointer;
`;

const ContentWrapper = styled.div`
  ${sharedWrapperStyles};
`;

const ContentStart = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: flex-start;
`;

const Radio = styled.div<{
  $hasThumbnail: boolean;
  $large: boolean;
  $selected: boolean;
}>`
  background-color: ${COLORS.White};
  border-color: ${({$hasThumbnail, $selected}) =>
    $hasThumbnail
      ? 'transparent'
      : `${$selected ? COLORS.Black : COLORS.Grey1}`};
  border-style: solid;
  border-width: ${({$hasThumbnail, $selected}) =>
    $hasThumbnail ? '0' : `${$selected ? '2px' : '1px'}`};
  border-radius: 0.625rem;
  box-sizing: border-box;
  height: 1.25rem;
  margin-right: ${({$large}) => ($large ? '1.25rem' : '0.625rem')};
  overflow: hidden;
  position: relative;
  width: 1.25rem;

  &:after {
    background-color: ${({$hasThumbnail}) =>
      $hasThumbnail ? COLORS.White : COLORS.Black};
    border-radius: 0.25rem;
    content: '';
    display: ${({$selected}) => ($selected ? 'block' : 'none')};
    height: 0.5rem;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 0.5rem;
  }
`;

// ❌ 🚨 Why are we using an styled image in here and not UI-Image?
// eslint-disable-next-line local-rules/prefer-image-component-over-html-image-tag
const Thumbnail = styled.img`
  border: none;
  height: inherit;
  object-fit: contain;
  width: inherit;
`;

const Label = styled.span<{$large: boolean; $selected: boolean}>`
  color: ${COLORS.Black};
  font-size: ${({$large}) => ($large ? FONT_SIZES.px16 : FONT_SIZES.px14)};
  font-weight: ${({$selected}) =>
    $selected ? FONT_WEIGHTS.bold : FONT_WEIGHTS.mediumBold};
  line-height: 1;
  margin-bottom: 2px;
`;
